import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { BehaviorSubject, combineLatest, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { authRoutesNames } from 'src/core/routes/auth/auth.routes.names';
import { coreRoutesNames } from 'src/core/routes/core.routes.names';
import { JwtHelperService } from 'src/core/services';
import { AuthStore } from 'src/core/stores';
import { passwordsMatchValidator } from '../validators/passwords-match.validator';

@Component({
  selector: 'core-reset-password',
  standalone: true,
  imports: [FormsModule, CommonModule, TranslocoDirective, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reset-password.component.html',
})
export class ResetPasswordComponent {
  private route = inject(ActivatedRoute);
  private authStore = inject(AuthStore);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private jwtHelperService = inject(JwtHelperService);

  resetPasswordForm!: FormGroup;
  uuid!: string;
  token!: string;
  showPassword = false;
  passwordReset = false;
  showAlert = false;
  genericError$$ = new BehaviorSubject(false);

  constructor() {
    const jwtHelperService = this.jwtHelperService;

    this.resetPasswordForm = this.initFormValidation();
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([pathParams, queryParams]) => {
          return {
            uuid: pathParams.get('uuid'),
            token: queryParams.get('token'),
          };
        }),
      )
      .subscribe((data) => {
        if (data.token && jwtHelperService.isTokenExpired(data.token)) {
          this.router.navigate(
            [coreRoutesNames.AUTH, authRoutesNames.FORGOT_PASSWORD],
            { state: { isResetPasswordRedirect: true } },
          );
        } else {
          this.uuid = data.uuid!;
          this.token = data.token!;
        }
      });
  }

  private initFormValidation(): FormGroup {
    //min 12 characters, at least one uppercase, one lowercase, one number and one special character
    return new FormGroup(
      {
        resetPasswordFormPassword: new FormControl('', {
          validators: [
            Validators.required,
            Validators.pattern(
              /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{12,128}$/,
            ),
          ],
        }),
        resetPasswordFormConfirmPassword: new FormControl('', {
          validators: [
            Validators.required,
            Validators.pattern(
              /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{12,128}$/,
            ),
          ],
        }),
      },
      {
        validators: passwordsMatchValidator(
          'resetPasswordFormPassword',
          'resetPasswordFormConfirmPassword',
        ),
      },
    );
  }

  get resetPasswordFormPassword() {
    return this.resetPasswordForm.get('resetPasswordFormPassword');
  }

  get resetPasswordFormConfirmPassword() {
    return this.resetPasswordForm.get('resetPasswordFormConfirmPassword');
  }

  getInputType(): string {
    return this.showPassword ? 'text' : 'password';
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  updatePassword() {
    this.authStore.setIsLoading(true);
    this.passwordReset = false;
    const password = this.resetPasswordFormPassword?.value || undefined;

    this.authStore
      .resetPasswordByToken(this.uuid, this.token, password)
      .pipe(
        catchError((err) => {
          this.passwordReset = false;
          this.authStore.setIsLoading(false);
          this.cdr.detectChanges();
          if (err.status === 401) {
            this.router.navigate(
              [coreRoutesNames.AUTH, authRoutesNames.FORGOT_PASSWORD],
              { state: { isResetPasswordRedirect: true } },
            );
          } else {
            this.genericError$$.next(true);
            this.showAlert = true;
            this.cdr.detectChanges();
          }
          return of();
        }),
      )
      .subscribe({
        next: (result) => {
          this.passwordReset = result;
          this.authStore.setIsLoading(false);
          this.cdr.detectChanges();
        },
      });
  }

  goHome() {
    this.router.navigate(['/']);
  }
}
